
(() => {
  if (customElements.get('scarles-search-hero')) return;

  class ScarlesSearchHero extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.initialised = true;
      this.events = new AbortController();

      const on = (target, event, fn) => {
        target.addEventListener(event, fn, {
          signal: this.events.signal
        });
      };

      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('.sc-finder__results');
      this.status = this.querySelector('.sc-finder__status');
      this.example = this.querySelector('.sc-finder__example');
      this.pause = this.querySelector('.sc-finder__pause');

      this.prompts = [
        ...this.querySelectorAll('.sc-finder__prompts span')
      ]
        .map(el => el.textContent.trim())
        .filter(Boolean);

      if (!this.prompts.length) {
        this.prompts = [
          'Honda Civic coilovers',
          'HKB boss kit'
        ];
      }

      this.index = 0;
      this.sequence = 0;
      this.request = null;
      this.debounce = null;
      this.rotation = null;
      this.trackingTimer = null;
      this.lastQuery = '';
      this.composing = false;

      /* ---------------------------------
         Predictive search tracking
      --------------------------------- */

      this.trackingDelay = 1500;
      this.trackedTerms = new Set();

      this.motion = matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

      this.paused = this.motion.matches;
      this.example.textContent = this.prompts[0];

      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.updatePause();

      /* ---------------------------------
         Rotating example searches
      --------------------------------- */

      on(this.pause, 'click', () => {
        this.paused = !this.paused;
        this.updatePause();
      });

      on(this.motion, 'change', () => {
        this.paused = this.motion.matches;
        this.updatePause();
      });

      this.rotation = setInterval(() => {
        if (
          this.paused ||
          this.input.value ||
          this.input === document.activeElement ||
          this.example === document.activeElement ||
          document.hidden
        ) {
          return;
        }

        this.index =
          (this.index + 1) % this.prompts.length;

        this.example.textContent =
          this.prompts[this.index];

        this.example.classList.remove(
          'sc-finder__enter'
        );

        void this.example.offsetWidth;

        this.example.classList.add(
          'sc-finder__enter'
        );
      }, Number(this.dataset.interval) || 5000);

      /* ---------------------------------
         Search input events
      --------------------------------- */

      on(this.input, 'input', () => {
        if (this.composing) return;
        this.schedule();
      });

      on(this.input, 'compositionstart', () => {
        this.composing = true;
        this.clearTrackingTimer();
      });

      on(this.input, 'compositionend', () => {
        this.composing = false;
        this.schedule();
      });

      on(this.input, 'focus', () => {
        const query = this.input.value.trim();

        if (
          query.length >= 2 &&
          query === this.lastQuery &&
          this.results.hasChildNodes()
        ) {
          this.openResults();
        }
      });

      /* ---------------------------------
         Example search click
      --------------------------------- */

      on(this.example, 'click', () => {
        this.input.value = this.prompts[this.index];
        this.input.focus();
        this.schedule(0);
      });

      /* ---------------------------------
         Escape key handling
      --------------------------------- */

      on(this, 'keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();

          this.input.value = '';
          this.schedule(0);
          this.closeResults();
        }
      });

      /* ---------------------------------
         Click outside to close dropdown
      --------------------------------- */

      on(document, 'pointerdown', event => {
        if (!this.contains(event.target)) {
          this.closeResults();
        }
      });

      /* ---------------------------------
         Native search submission
      --------------------------------- */

      on(this.form, 'submit', () => {
        this.clearTrackingTimer();
        this.closeResults();
      });
    }

    /* ---------------------------------
       Play / Pause button
    --------------------------------- */

    updatePause() {
      const label = this.paused ? 'Play' : 'Pause';

      this.pause.textContent = label;

      this.pause.setAttribute(
        'aria-label',
        `${label} example searches`
      );
    }

    /* ---------------------------------
       Dropdown controls
    --------------------------------- */

    openResults() {
      if (!this.results.hasChildNodes()) return;

      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    /* ---------------------------------
       Predictive search tracking helpers
    --------------------------------- */

    clearTrackingTimer() {
      if (this.trackingTimer !== null) {
        clearTimeout(this.trackingTimer);
        this.trackingTimer = null;
      }
    }

    isTrackableSearch(query) {
      if (query.length < 2) return false;

      // Exclude obvious email addresses.
      const emailPattern =
        /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

      if (emailPattern.test(query)) return false;

      // Exclude obvious phone-number-like searches.
      const compact = query.replace(/[\s()+.-]/g, '');

      if (/^\d{7,15}$/.test(compact)) {
        return false;
      }

      return true;
    }

    scheduleTracking(query, resultsFound, sequence) {
      this.clearTrackingTimer();

      if (!this.isTrackableSearch(query)) return;

      const normalised = query
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

      if (this.trackedTerms.has(normalised)) return;

      this.trackingTimer = setTimeout(() => {
        this.trackingTimer = null;

        if (!this.isConnected) return;

        // Ignore outdated searches.
        if (sequence !== this.sequence) return;

        // Only track when the input still matches.
        if (
          this.input.value.trim().replace(/\s+/g, ' ')
            .toLowerCase() !== normalised
        ) {
          return;
        }

        // Exclude searches that are no longer active.
        if (this.results.hidden) return;

        const analytics = window.Shopify?.analytics;

        if (
          !analytics ||
          typeof analytics.publish !== 'function'
        ) {
          return;
        }

        try {
          analytics.publish(
            'scarles:predictive_search',
            {
              search_term: query,
              results_found: resultsFound
            }
          );

          this.trackedTerms.add(normalised);

        } catch (error) {
          // Tracking errors must never affect search.
          console.warn(
            'Scarles predictive search tracking unavailable',
            error
          );
        }
      }, this.trackingDelay);
    }

    /* ---------------------------------
       Debounced search
    --------------------------------- */

    schedule(delay = 300) {
      clearTimeout(this.debounce);
      this.clearTrackingTimer();

      if (this.request) {
        this.request.abort();
        this.request = null;
      }

      const sequence = ++this.sequence;
      const query = this.input.value.trim();

      this.lastQuery = '';

      this.results.replaceChildren();

      this.results.setAttribute(
        'aria-busy',
        'false'
      );

      this.status.textContent = '';
      this.closeResults();

      if (
        query.length < 2 ||
        this.input.disabled
      ) {
        return;
      }

      this.status.textContent = 'Searching…';

      this.debounce = setTimeout(() => {
        this.search(query, sequence);
      }, delay);
    }

    /* ---------------------------------
       Full search URL
    --------------------------------- */

    fullSearch(query) {
      const url = new URL(
        this.form.action,
        location.origin
      );

      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set(
        'options[prefix]',
        'last'
      );

      return url.href;
    }

    addFullSearch(query) {
      const link = document.createElement('a');

      link.className = 'sc-finder__all';
      link.href = this.fullSearch(query);

      link.textContent =
        `View all search results for “${query}” →`;

      this.results.append(link);
    }

    /* ---------------------------------
       Shopify predictive search
    --------------------------------- */

    async search(query, sequence) {
      const request = new AbortController();
      this.request = request;

      const timeout = setTimeout(() => {
        request.abort();
      }, 10000);

      this.results.setAttribute(
        'aria-busy',
        'true'
      );

      try {
        const url = new URL(
          this.dataset.suggestUrl,
          location.origin
        );

        url.searchParams.set('q', query);

        url.searchParams.set(
          'resources[type]',
          'product,collection'
        );

        url.searchParams.set(
          'resources[limit]',
          '8'
        );

        url.searchParams.set(
          'resources[limit_scope]',
          'each'
        );

        url.searchParams.set(
          'resources[options][unavailable_products]',
          'last'
        );

        url.searchParams.set(
          'section_id',
          this.dataset.resultsSection
        );

        const response = await fetch(url, {
          signal: request.signal,
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(
            `Search returned ${response.status}`
          );
        }

        const html = await response.text();

        if (
          sequence !== this.sequence ||
          !this.isConnected
        ) {
          return;
        }

        const doc = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const content = doc.querySelector(
          '[data-sc-results]'
        );

        if (!content) {
          throw new Error(
            'Search results section missing'
          );
        }

        this.results.replaceChildren(
          document.importNode(content, true)
        );

        this.addFullSearch(query);

        const count = Math.min(
          Number(content.dataset.count) || 0,
          4
        );

        this.lastQuery = query;

        this.status.textContent = count
          ? `${count} product suggestions shown. View all results for more.`
          : '';

        this.openResults();

        /* ---------------------------------
           Schedule predictive tracking
           after successful Shopify response
        --------------------------------- */

        this.scheduleTracking(
          query,
          count > 0,
          sequence
        );

      } catch (error) {
        if (
          sequence !== this.sequence ||
          !this.isConnected
        ) {
          return;
        }

        this.results.replaceChildren();

        this.status.textContent =
          'Suggestions are unavailable. Select Find it or view the full search results.';

        this.addFullSearch(query);

        this.lastQuery = query;
        this.openResults();

        // Do not track failed predictive requests.

      } finally {
        clearTimeout(timeout);

        if (this.request === request) {
          this.request = null;
        }

        if (sequence === this.sequence) {
          this.results.setAttribute(
            'aria-busy',
            'false'
          );
        }
      }
    }

    /* ---------------------------------
       Shopify theme editor cleanup
    --------------------------------- */

    disconnectedCallback() {
      clearInterval(this.rotation);
      clearTimeout(this.debounce);
      this.clearTrackingTimer();

      if (this.request) {
        this.request.abort();
        this.request = null;
      }

      if (this.events) {
        this.events.abort();
      }

      this.sequence++;
      this.initialised = false;
    }
  }

  customElements.define(
    'scarles-search-hero',
    ScarlesSearchHero
  );
})();
